import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addExternalPolice,
  editExternalPolice,
} from "services/externalUserService";
import { ExternalPolice, PoliceStation, Roles } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  action: "Add" | "Edit";
  externalPolice?: ExternalPolice;
  policeStations: PoliceStation[];
  onClose: (refetch: boolean) => void;
};

const defaultValue: ExternalPolice = {
  title: "",
  full_name: "",
  police_id_number: "",
  station_address: "",
  phone_number: "",
  username: "",
  police_station_id: 0,
  email: "",
  role: "police",
  password: "",
  confirm_password: "",
  assigned_by_id: "",
  police_station: "",
};

const ExternalPoliceForm: FC<Props> = ({
  action,
  externalPolice,
  policeStations,
  onClose,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<ExternalPolice>(
    externalPolice ?? defaultValue
  );

  useEffect(() => {
    if (externalPolice) {
      setFormState(externalPolice);
    }
  }, [externalPolice]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.title) return "Title is Required.";
    else if (!formState.full_name) return "Full Name is Required.";
    // else if (!formState.police_id_number)
    //   return "Police ID Number is Required.";
    else if (!formState.station_address) return "Station Address is Required.";
    else if (!formState.password) return "Password is Required.";
    else if (!formState.role) return "Role is Required.";
    else if (formState.password !== formState.confirm_password)
      return "Password did not match is Required.";

    return "";
  };

  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isExternalCoordinator = hasRole(loginData?.roles, [Roles.member]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    // Submit the FormData
    const submitAction =
      action === "Edit" ? editExternalPolice : addExternalPolice;
    submitAction({
      ...formState,
      role: isCoordinator
        ? "police_coordinator"
        : isExternalCoordinator
        ? "police"
        : formState.role,

      assigned_by_id:
        action === "Add" ? loginData?.user_id + "" : formState?.assigned_by_id,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success"
        );
        setFormState(defaultValue);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error"
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {action} Police {isCoordinator ? "Coordinator" : ""}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          Lorem Ipsum is a publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Title
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="title"
                value={formState?.title}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Full Name
              </label>
              <input
                type="text"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="full_name"
                value={formState?.full_name}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Police Station
              </label>
              <select
                required
                name="police_station_id"
                value={formState?.police_station_id ?? ""}
                onChange={handleChange}
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              >
                {policeStations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Police ID
              </label>
              <input
                type="number"
                className="bg-[#9c7777] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="police_id_number"
                value={
                  typeof formState?.police_id_number === "string" ||
                  typeof formState?.police_id_number === "number"
                    ? formState.police_id_number
                    : ""
                }
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Station Address
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="station_address"
                value={formState?.station_address}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Phone Number
              </label>
              <input
                type="phone"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="phone_number"
                value={formState?.phone_number}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Username
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="username"
                value={formState?.username}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Email
              </label>
              <input
                type="email"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="email"
                value={formState?.email}
                onChange={handleChange}
              />
            </div>
            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Role
              </label>
              <select
                required
                name="role"
                value={formState?.role ?? ""}
                onChange={handleChange}
                className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
              >
                <option value="police">Police</option>
                <option value="police_coordinator">Police Coordinator</option>
              </select>
            </div> */}
          </div>

          <div className="grid grid-cols-2 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Password
              </label>
              <input
                type="password"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="password"
                value={formState?.password}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="confirm_password"
                value={formState?.confirm_password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                action + " Police"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExternalPoliceForm;
